import type { NightWindow } from '../city/NightWindow'
import type { Mode } from '../schema/types'
import { nightWindowState } from './nightWindowState'

/** What the night auto-switch does at one moment (TASK-1136). */
export interface NightAutoSwitchDecision {
  /** The mode the map should be in; `null` — leave it, a manual choice holds. */
  readonly mode: Mode | null
  /** Until when a manual choice holds (epoch ms), `null` when none does. */
  readonly overrideUntil: number | null
  /** When to decide again (epoch ms): the window's next opening or close. */
  readonly nextCheckAt: number
}

/**
 * The auto-switch rule: inside the city's night window the map is in night
 * mode, outside it in normal mode — unless the person toggled it by hand,
 * which holds until the next opening or close of the window
 * ({@link nightOverrideUntil}). Pure.
 */
export function decideNightMode(
  now: Date,
  window: NightWindow,
  timeZone: string,
  overrideUntil: number | null,
): NightAutoSwitchDecision {
  const state = nightWindowState(now, window, timeZone)
  const nextCheckAt = state.nextBoundary.getTime()
  if (overrideUntil !== null && now.getTime() < overrideUntil) {
    return { mode: null, overrideUntil, nextCheckAt: Math.min(nextCheckAt, overrideUntil) }
  }
  return { mode: state.active ? 'night' : 'normal', overrideUntil: null, nextCheckAt }
}

/** A manual toggle at `now` holds until the window's next opening or close. */
export function nightOverrideUntil(now: Date, window: NightWindow, timeZone: string): number {
  return nightWindowState(now, window, timeZone).nextBoundary.getTime()
}
